import React, { useState, useEffect, useCallback } from 'react';
import { ScrollText, Loader2, Search, Filter } from 'lucide-react';
import { auditAPI, exportAPI } from '../../services/api';
import ExportMenu from '../../components/ExportMenu';
import clsx from 'clsx';

const ACTION_COLOR = (action) => {
  if (action?.includes('DELETE') || action?.includes('DEACTIVATE')) return 'bg-red-100 text-red-700';
  if (action?.includes('CREATE') || action?.includes('LOGIN')) return 'bg-emerald-100 text-emerald-700';
  if (action?.includes('UPDATE') || action?.includes('GENERATED')) return 'bg-blue-100 text-blue-700';
  return 'bg-gray-100 text-gray-600';
};

export default function AuditPage() {
  const [logs, setLogs]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [actions, setActions]   = useState([]);
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate]     = useState('');
  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await auditAPI.list({
        page, limit,
        action:      actionFilter || undefined,
        entity_type: entityFilter || undefined,
        from:        fromDate || undefined,
        to:          toDate   || undefined,
      });
      setLogs(data.data.items || data.data);
      setTotal(data.data.pagination?.total || 0);
    } catch {} finally { setLoading(false); }
  }, [page, actionFilter, entityFilter, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    auditAPI.actions().then(({ data }) => setActions(data.data || [])).catch(() => {});
  }, []);

  useEffect(() => { setPage(1); }, [actionFilter, entityFilter, fromDate, toDate]);

  const totalPages = Math.ceil(total / limit);

  const ENTITY_TYPES = ['asset', 'maintenance_records', 'ppa_contracts', 'regulatory_filings', 'carbon_emissions', 'user', 'esg_reports', 'csrd_reports'];

  return (
    <div className="p-6 max-w-[1200px] mx-auto space-y-5">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Audit Log</h1>
        <p className="text-sm text-gray-500">Complete record of all user actions — {total.toLocaleString()} entries</p>
        <ExportMenu exports={[{ label:'Export Excel', apiFn: exportAPI.auditExcel, filename:'audit-log.xlsx' }]}/>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs font-medium text-gray-500 mb-1">Action</label>
          <select value={actionFilter} onChange={e => setActionFilter(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/30">
            <option value="">All Actions</option>
            {actions.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs font-medium text-gray-500 mb-1">Entity Type</label>
          <select value={entityFilter} onChange={e => setEntityFilter(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/30">
            <option value="">All Entities</option>
            {ENTITY_TYPES.map(e => <option key={e} value={e}>{e.replace(/_/g,' ')}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30"/>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30"/>
        </div>
        <button onClick={() => { setActionFilter(''); setEntityFilter(''); setFromDate(''); setToDate(''); }}
          className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors">
          Clear
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                {['Timestamp','User','Action','Entity','IP Address'].map(h => (
                  <th key={h} className="px-4 py-3 text-left font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={5} className="text-center py-12">
                  <Loader2 size={20} className="animate-spin text-gray-400 mx-auto"/>
                </td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-12 text-gray-400 text-sm">No audit logs found.</td></tr>
              ) : logs.map((log, i) => (
                <tr key={log.id} className={clsx('hover:bg-hover transition-colors', i%2===0?'bg-white':'bg-surface')}>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                    {new Date(log.created_at).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })}
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 text-xs">{log.user_name || 'System'}</p>
                    <p className="text-xs text-gray-400">{log.user_email || ''}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', ACTION_COLOR(log.action))}>
                      {log.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs">
                    {log.entity_type && <span className="capitalize">{log.entity_type.replace(/_/g,' ')}</span>}
                    {log.entity_id && <span className="text-gray-400"> #{log.entity_id}</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs font-mono">{log.ip_address || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-600">
            <span>{total.toLocaleString()} total · Page {page} of {totalPages}</span>
            <div className="flex gap-1">
              <button disabled={page===1} onClick={() => setPage(p=>p-1)}
                className="px-3 py-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 text-xs">Previous</button>
              <button disabled={page===totalPages} onClick={() => setPage(p=>p+1)}
                className="px-3 py-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 text-xs">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
